/**
 * نقشه‌ای که فقط یه کار می‌کنه: یه نقطه انتخاب کن.
 *
 * ورودیِ متنیِ «کجا» برای خیلی از جاها کافی نیست — «کنارِ زمینِ چمن، پشتِ
 * ساختمونِ آبی» آدرس نیست ولی روی نقشه یه نقطه‌ی دقیقه. این فایل همون
 * صفحه‌ی HTMLی رو می‌سازه که هر دو کلاینت داخلِ یه قاب نشونش می‌دن:
 * موبایل توی `WebView` و سایت توی `<iframe srcdoc>`.
 *
 * چرا این‌جا و نه توی هر اپ: صفحه‌ی نقشه یه رشته‌ی خالصه، بدونِ هیچ ربطی به
 * ری‌اکت‌نیتیو یا DOM — و اگه هر اپ نسخه‌ی خودش رو داشت، اولین تغییر (زومِ
 * پیش‌فرض، رنگِ نشانگر، رفتارِ کشیدن) یکی‌شون رو جا می‌ذاشت.
 *
 * همون Leafletِ نقشه‌ی کشف (features/discover/MapView.tsx در اپِ موبایل) و
 * همون کاشی‌های OpenStreetMap: هیچ کلیدی نمی‌خواد، پس هیچ حسابِ تازه‌ای هم
 * لازم نداره.
 *
 * پیامِ بیرون همیشه یه چیزه:  `{"type":"pick","lat":45.4,"lng":-75.6}`
 * و از هر دو راهی که ممکنه می‌ره بیرون (ReactNativeWebView و postMessageِ
 * معمولی)، چون همین یه صفحه توی هر دو قاب اجرا می‌شه.
 */

/** نقطه‌ی پیش‌فرضِ نقشه وقتی هنوز چیزی انتخاب نشده — مرکزِ اتاوا. */
export const DEFAULT_CENTER = { lat: 45.4215, lng: -75.6972 };

/**
 * تگ‌های Leaflet — **یک نسخه، دو صفحه**.
 *
 * `mapView.ts` هم همین را لازم دارد و دو رشته‌ی جدا یعنی روزی که یکی به
 * نسخه‌ی تازه برود و آن یکی نه. همان دلیلی که `CA_PROVINCES` را از
 * `address.ts` بیرون کشید.
 */
export const LEAFLET_TAGS =
  '<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css">'
  + '<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>';

export interface PickedPoint { lat: number; lng: number }

/** عددِ سالم و توی بازه‌ی معنی‌دار. مرکاتور بیرونِ ±۸۵ بی‌نهایت می‌ده. */
export const usablePoint = (p?: Partial<PickedPoint> | null): p is PickedPoint =>
  !!p && Number.isFinite(p.lat) && Number.isFinite(p.lng)
  && Math.abs(p.lat as number) <= 85 && !(p.lat === 0 && p.lng === 0);

/**
 * صفحه‌ی انتخابِ نقطه.
 *
 * `value` نقطه‌ی فعلیه (اگه قبلاً چیزی انتخاب شده)، `center` جاییه که نقشه
 * ازش شروع می‌شه — معمولاً مختصاتِ شهرِ جاری، تا کاربر مجبور نباشه از اقیانوس
 * تا شهرش اسکرول کنه.
 */
export function mapPickerHtml(
  value: PickedPoint | null,
  center: PickedPoint = DEFAULT_CENTER,
): string {
  const start = value ?? center;
  const zoom = value ? 16 : 12;

  return `<!DOCTYPE html>
<html><head>
<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1">
${LEAFLET_TAGS}
<style>html,body,#map{height:100%;margin:0;padding:0}</style>
</head><body><div id="map"></div><script>
  function out(msg) {
    var s = JSON.stringify(msg);
    // موبایل: WebView. سایت: iframe. همون صفحه، دو تا قاب.
    if (window.ReactNativeWebView) window.ReactNativeWebView.postMessage(s);
    else if (window.parent && window.parent !== window) window.parent.postMessage(s, '*');
  }

  // خطای این صفحه هیچ کنسولی نداره که کسی ببیندش؛ می‌ره بیرون تا خودِ فرم
  // بتونه بگه «نقشه بالا نیامد» — و کاربر بفهمه چرا کادر خالیه.
  window.onerror = function (m, s, l) {
    out({ type: 'error', message: String(m) + ' @' + String(l) });
    return false;
  };
  if (!window.L) window.onerror('leaflet did not load', '', 0);

  var map = L.map('map').setView([${start.lat}, ${start.lng}], ${zoom});
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 19, attribution: '© OpenStreetMap'
  }).addTo(map);

  var pin = null;
  function place(lat, lng, tell) {
    if (pin) { pin.setLatLng([lat, lng]); }
    else {
      // نشانگرِ قابلِ کشیدن: بارِ اول با یه لمس می‌شینه، بعدش با کشیدن
      // دقیق‌تر می‌شه — همون کاری که هر کسی روی هر نقشه‌ای انتظار داره.
      pin = L.marker([lat, lng], { draggable: true }).addTo(map);
      pin.on('dragend', function () {
        var p = pin.getLatLng();
        out({ type: 'pick', lat: p.lat, lng: p.lng });
      });
    }
    if (tell) out({ type: 'pick', lat: lat, lng: lng });
  }

  map.on('click', function (e) { place(e.latlng.lat, e.latlng.lng, true); });
  ${value ? `place(${value.lat}, ${value.lng}, false);` : ''}
</script></body></html>`;
}
